// Utils
import { isPlatformIframe, isPlatformTablet } from '../../platform/utils/platformDetailsUtils';

// Types
import { Rect, RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import { Point } from '../../../common/maths/geometry/pointTypes';
import { PlatformDetails } from '../../../common/platform/platformTypes';

export const EMPTY_CANVAS_SIZE = { width: 0, height: 0 };

const PADDING_IFRAME = { x: 5, y: 5 };
const PADDING_ZOOMED_OUT = { x: 5, y: 5 };
export const CANVAS_ELEMENT_PADDING_STANDARD = { x: 50, y: 40 };

const PADDING_TABLET = { x: CANVAS_ELEMENT_PADDING_STANDARD.x, y: 50 };

/**
 * Determines the canvas padding to use at the bottom right of elements depending on the context that the board
 * is being displayed in.
 */
export const getCanvasDocumentBottomRightPaddingGridUnits = (
    zoomScale: number,
    platformDetails: PlatformDetails,
): Point => {
    if (isPlatformIframe(platformDetails)) return PADDING_IFRAME;
    if (zoomScale < 1) return PADDING_ZOOMED_OUT;

    // Ensure that iPads and Android tablets have enough space below the element for the virtual keyboard
    // NOTE: This is a worst case scenario (500px on the 12.9" iPad Pro). There's no real disadvantage to having
    //  too much extra space at the bottom, it just needs to be enough for the largest keyboard
    if (isPlatformTablet(platformDetails)) return PADDING_TABLET;
    return CANVAS_ELEMENT_PADDING_STANDARD;
};

/**
 * Determines the next canvas size by inspecting all element measurements and:
 * - Adding padding around them if the current scale is >= 100%
 * - Adding padding around them if it's bigger than the previous canvas size
 * - Using the previous canvas size otherwise
 *
 * TODO-ZOOM - Determine if this really should be a calculated value and also what should
 *  happen when returning to 100%
 */
export const calculateNextCanvasSize = (
    prevCanvasSize: RectSize,
    canvasElementsBoundingRect: Rect,
    gridSize: number,
    canvasElementIds: Array<string>,
    zoomScale: number,
    platformDetails: PlatformDetails,
): RectSize => {
    // Use the maximums of the canvas elements bounding rect.
    // NOTE: this is *not* the width/height of the bounding rect as that might not start at 0
    const newCanvasSize = {
        width: canvasElementsBoundingRect.right || 0,
        height: canvasElementsBoundingRect.bottom || 0,
    };

    const canvasPadding = getCanvasDocumentBottomRightPaddingGridUnits(zoomScale, platformDetails);

    const nextWidth =
        zoomScale >= 1 || newCanvasSize.width > prevCanvasSize.width
            ? Math.round(Math.max(prevCanvasSize.width, newCanvasSize.width + canvasPadding.x * gridSize))
            : prevCanvasSize.width;

    const nextHeight =
        zoomScale >= 1 || newCanvasSize.height > prevCanvasSize.height
            ? Math.round(Math.max(prevCanvasSize.height, newCanvasSize.height + canvasPadding.y * gridSize))
            : prevCanvasSize.height;

    return {
        width: nextWidth,
        height: nextHeight,
    };
};

/**
 * Ensures the canvas document will not be smaller than the viewport.
 */
export const getMinimumCanvasDocumentSize = (canvasDocumentSize: RectSize, canvasViewportSize: RectSize): RectSize => {
    if (!canvasDocumentSize || !canvasViewportSize) return canvasDocumentSize;

    if (canvasDocumentSize.width < canvasViewportSize.width || canvasDocumentSize.height < canvasViewportSize.height) {
        return {
            width: Math.max(canvasDocumentSize.width, canvasViewportSize.width),
            height: Math.max(canvasDocumentSize.height, canvasViewportSize.height),
        };
    }

    return canvasDocumentSize;
};
